<style>
    #privacy{
        text-align: left !important;
        direction: ltr !important;
    }
</style>

<template>
  <div id="privacy" class="LegalPage py-12 bg-gray-50 min-h-screen align">
    <div class="max-w-4xl mx-auto px-4">

      <!-- Page Header -->
      <div class="mb-10 border-b border-gray-200 pb-6">
        <h1 class="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p class="text-sm text-gray-500">Last updated: September 21, 2023</p>
      </div>

      <!-- Intro Section -->
      <section class="mb-10">
        <p class="text-gray-700 leading-relaxed mb-4">
          This Privacy Policy describes our policies and procedures on the collection, use, and
          disclosure of your information when you use the Service. It also informs you about
          your privacy rights and how the law protects you.
        </p>
        <p class="text-gray-700 leading-relaxed">
          We use your personal data to provide and improve the Service. By using the Service,
          you agree to the collection and use of information in accordance with this Privacy Policy.
        </p>
      </section>

      <!-- Section 1: Interpretation and Definitions -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          1. Interpretation and Definitions
        </h2>

        <h3 class="text-xl font-semibold text-gray-800 mb-3">Interpretation</h3>
        <p class="text-gray-700 leading-relaxed mb-6">
          Words with capitalized initial letters have meanings defined under the following
          conditions. These definitions apply equally whether they appear in singular or plural form.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3">Definitions</h3>
        <p class="text-gray-700 mb-4">For the purposes of this Privacy Policy:</p>

        <ul class="space-y-4">
          <li
            v-for="definition in definitions"
            :key="definition.term"
            class="bg-white rounded-lg p-4 shadow-sm border border-gray-100"
          >
            <span class="font-semibold text-gray-900">{{ definition.term }}: </span>
            <span class="text-gray-700">{{ definition.description }}</span>
          </li>
        </ul>
      </section>

      <!-- Section 2: Collecting and Using Your Personal Data -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          2. Collecting and Using Your Personal Data
        </h2>

        <!-- Types of Data -->
        <h3 class="text-xl font-semibold text-gray-800 mb-3">Types of Data Collected</h3>

        <div class="mb-6">
          <h4 class="text-lg font-semibold text-gray-800 mb-2">Personal Data</h4>
          <p class="text-gray-700 leading-relaxed mb-3">
            While using our Service, we may ask you to provide certain personally identifiable
            information that can be used to contact or identify you. This may include, but is
            not limited to:
          </p>
          <ul class="list-disc list-inside space-y-2 text-gray-700 bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
            <li v-for="item in personalDataItems" :key="item">{{ item }}</li>
          </ul>
        </div>

        <!-- Usage Data -->
        <div class="mb-6">
          <h4 class="text-lg font-semibold text-gray-800 mb-2">Usage Data</h4>
          <p class="text-gray-700 leading-relaxed mb-3">
            Usage Data is collected automatically when using the Service. It may include
            information such as your device's IP address, browser type and version, the pages
            you visit on our Service, the time and date of your visit, time spent on pages,
            unique device identifiers, and other diagnostic data.
          </p>
          <p class="text-gray-700 leading-relaxed mb-3">
            When you access the Service via a mobile device, we may automatically collect
            information including your mobile device type, unique device ID, mobile IP address,
            mobile operating system, mobile browser type, and other diagnostic data.
          </p>
          <p class="text-gray-700 leading-relaxed">
            We may also collect information that your browser sends whenever you visit our
            Service or when you access it through a mobile device.
          </p>
        </div>

        <!-- Third-Party Social Media -->
        <div class="mb-6">
          <h4 class="text-lg font-semibold text-gray-800 mb-2">
            Information from Third-Party Social Media Services
          </h4>
          <p class="text-gray-700 leading-relaxed mb-3">
            The Company allows you to create an account and log in using the following
            Third-Party Social Media Services:
          </p>
          <ul class="flex gap-3 mb-4">
            <li
              v-for="provider in socialProviders"
              :key="provider"
              class="bg-blue-50 text-blue-700 font-medium px-4 py-1 rounded-full text-sm border border-blue-100"
            >
              {{ provider }}
            </li>
          </ul>
          <p class="text-gray-700 leading-relaxed mb-3">
            If you register through or grant us access to a Third-Party Social Media Service,
            we may collect Personal Data already associated with that account, such as your
            name, email address, activities, or contact list.
          </p>
          <p class="text-gray-700 leading-relaxed">
            You may also share additional information with us through your Third-Party Social
            Media account. By choosing to provide such information, you grant the Company
            permission to use, share, and store it in accordance with this Privacy Policy.
          </p>
        </div>

        <!-- Tracking Technologies & Cookies -->
        <div class="mb-6">
          <h4 class="text-lg font-semibold text-gray-800 mb-2">
            Tracking Technologies and Cookies
          </h4>
          <p class="text-gray-700 leading-relaxed mb-4">
            We use Cookies and similar tracking technologies to track activity on our Service
            and store certain information. These technologies include beacons, tags, and scripts
            to collect, track, and improve our Service.
          </p>

          <div class="space-y-4 mb-6">
            <div
              v-for="tech in trackingTechnologies"
              :key="tech.name"
              class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm"
            >
              <h5 class="font-semibold text-gray-900 mb-1">{{ tech.name }}</h5>
              <p class="text-gray-700 text-sm leading-relaxed">{{ tech.description }}</p>
            </div>
          </div>

          <p class="text-gray-700 leading-relaxed mb-4">
            Cookies can be <strong>Persistent</strong> (remaining on your device when you go
            offline) or <strong>Session</strong> (deleted when you close your browser). We use
            both types for the following purposes:
          </p>

          <div class="space-y-4">
            <div
              v-for="cookie in cookieTypes"
              :key="cookie.name"
              class="bg-white rounded-lg p-4 border border-gray-100 shadow-sm"
            >
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <h5 class="font-semibold text-gray-900">{{ cookie.name }}</h5>
                <span class="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-100">
                  {{ cookie.type }}
                </span>
              </div>
              <p class="text-gray-700 text-sm leading-relaxed">{{ cookie.purpose }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 3: Use of Personal Data -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          3. Use of Your Personal Data
        </h2>
        <p class="text-gray-700 mb-4">
          The Company may use your Personal Data for the following purposes:
        </p>
        <ul class="space-y-3 mb-6">
          <li
            v-for="use in dataUses"
            :key="use.title"
            class="flex gap-3 bg-white p-4 rounded-lg border border-gray-100 shadow-sm"
          >
            <span class="text-blue-500 mt-0.5">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9
                  10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
            <div>
              <span class="font-semibold text-gray-900">{{ use.title }}: </span>
              <span class="text-gray-700">{{ use.description }}</span>
            </div>
          </li>
        </ul>

        <h3 class="text-xl font-semibold text-gray-800 mb-3">Sharing Your Personal Information</h3>
        <p class="text-gray-700 mb-4">
          We may share your personal information in the following situations:
        </p>
        <ul class="space-y-3">
          <li
            v-for="share in dataSharingScenarios"
            :key="share.title"
            class="bg-white p-4 rounded-lg border border-gray-100 shadow-sm"
          >
            <span class="font-semibold text-gray-900">{{ share.title }}: </span>
            <span class="text-gray-700">{{ share.description }}</span>
          </li>
        </ul>
      </section>

      <!-- Section 4: Retention -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          4. Retention of Your Personal Data
        </h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          The Company will retain your Personal Data only for as long as necessary to fulfill
          the purposes outlined in this Privacy Policy, including meeting legal obligations,
          resolving disputes, and enforcing agreements.
        </p>
        <p class="text-gray-700 leading-relaxed">
          Usage Data is generally retained for a shorter period, unless it is used to strengthen
          the security or improve the functionality of our Service, or we are legally required
          to retain it for a longer period.
        </p>
      </section>

      <!-- Section 5: Transfer -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          5. Transfer of Your Personal Data
        </h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          Your information, including Personal Data, may be processed at the Company's
          operating offices or in any other location where the parties involved in processing
          are located. This means your data may be transferred to — and maintained on —
          computers located outside your state, province, country, or other governmental
          jurisdiction where data protection laws may differ from those in your jurisdiction.
        </p>
        <p class="text-gray-700 leading-relaxed mb-4">
          Your consent to this Privacy Policy, followed by your submission of such information,
          represents your agreement to that transfer.
        </p>
        <p class="text-gray-700 leading-relaxed">
          The Company will take all reasonably necessary steps to ensure your data is treated
          securely and in accordance with this Privacy Policy. No transfer of your Personal
          Data will occur to any organization or country without adequate security controls in place.
        </p>
      </section>

      <!-- Section 6: Disclosure -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          6. Disclosure of Your Personal Data
        </h2>

        <div class="space-y-6">
          <div
            v-for="disclosure in disclosureScenarios"
            :key="disclosure.title"
            class="bg-white rounded-lg p-5 border border-gray-100 shadow-sm"
          >
            <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ disclosure.title }}</h3>
            <p class="text-gray-700 leading-relaxed">{{ disclosure.description }}</p>
            <ul
              v-if="disclosure.items"
              class="list-disc list-inside mt-3 space-y-1 text-gray-700"
            >
              <li v-for="item in disclosure.items" :key="item">{{ item }}</li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Section 7: Security -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          7. Security of Your Personal Data
        </h2>
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
          <p class="text-gray-700 leading-relaxed">
            The security of your Personal Data is important to us. While we strive to use
            commercially acceptable means to protect your data, please be aware that no method
            of transmission over the Internet or electronic storage is 100% secure.
            We cannot guarantee absolute security.
          </p>
        </div>
      </section>

      <!-- Section 8: Children's Privacy -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          8. Children's Privacy
        </h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          Our Service does not address anyone under the age of 13. We do not knowingly collect
          personally identifiable information from anyone under 13. If you are a parent or
          guardian and believe your child has provided us with Personal Data, please contact us
          immediately.
        </p>
        <p class="text-gray-700 leading-relaxed">
          If we discover that we have collected Personal Data from anyone under 13 without
          verified parental consent, we will take steps to remove that information from our
          servers. Where applicable, we may require parental consent before collecting and
          using a child's information.
        </p>
      </section>

      <!-- Section 9: Third-Party Links -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          9. Links to Other Websites
        </h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          Our Service may contain links to third-party websites not operated by us. If you
          click on a third-party link, you will be directed to that site. We strongly advise
          you to review the Privacy Policy of every site you visit.
        </p>
        <p class="text-gray-700 leading-relaxed">
          We have no control over and assume no responsibility for the content, privacy
          policies, or practices of any third-party sites or services.
        </p>
      </section>

      <!-- Section 10: Data Deletion -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          10. Data Deletion Request
        </h2>
        <p class="text-gray-700 leading-relaxed mb-6">
          We value your privacy and are committed to handling your personal data with care.
          If you wish to request deletion of your personal data, please follow the process below.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div
            v-for="step in deletionSteps"
            :key="step.title"
            class="bg-white rounded-lg p-5 border border-gray-100 shadow-sm"
          >
            <div class="flex items-start gap-3">
              <span
                class="w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center
                justify-center font-bold text-sm flex-shrink-0"
              >
                {{ step.number }}
              </span>
              <div>
                <h4 class="font-semibold text-gray-900 mb-1">{{ step.title }}</h4>
                <p class="text-gray-700 text-sm leading-relaxed">{{ step.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact for Deletion -->
        <div class="bg-blue-50 border border-blue-100 rounded-lg p-5">
          <h4 class="font-semibold text-gray-900 mb-3">Customer Service Contact</h4>
          <ul class="space-y-2 text-gray-700">
            <li class="flex items-center gap-2">
              <svg class="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1
                  0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1
                  1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82
                  18 2 12.18 2 5V3z"
                />
              </svg>
              <span>+964 770 2000 545 | +964 750 2000 545</span>
            </li>
            <li class="flex items-center gap-2">
              <svg class="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2
                  0 00-1.997 1.884z"
                />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <a
                href="mailto:slaw@agoravision.com"
                class="text-blue-600 hover:underline"
              >
                slaw@agoravision.com
              </a>
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 11: Changes to Policy -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          11. Changes to This Privacy Policy
        </h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          We may update our Privacy Policy from time to time. We will notify you of any
          changes by posting the new Privacy Policy on this page and updating the
          "Last updated" date at the top.
        </p>
        <p class="text-gray-700 leading-relaxed">
          We will notify you via email and/or a prominent notice on our Service prior to
          any changes becoming effective. We encourage you to review this Privacy Policy
          periodically for updates.
        </p>
      </section>

      <!-- Section 12: Contact Us -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          12. Contact Us
        </h2>
        <p class="text-gray-700 mb-5">
          If you have any questions about this Privacy Policy, you can reach us through
          any of the following channels:
        </p>

        <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <ul class="divide-y divide-gray-100">
            <li
              v-for="contact in contactDetails"
              :key="contact.label"
              class="flex items-start gap-4 p-4"
            >
              <span class="text-blue-500 mt-0.5 flex-shrink-0">
                <component :is="contact.icon" class="w-5 h-5" />
              </span>
              <div>
                <span class="block text-sm font-semibold text-gray-500 mb-0.5">
                  {{ contact.label }}
                </span>
                <a
                  v-if="contact.href"
                  :href="contact.href"
                  class="text-blue-600 hover:underline text-sm"
                >
                  {{ contact.value }}
                </a>
                <span v-else class="text-gray-700 text-sm">{{ contact.value }}</span>
              </div>
            </li>
          </ul>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
export default {
  name: "LegalPage",

  data() {
    return {
      definitions: [
        {
          term: "Account",
          description:
            "A unique account created for you to access our Service or parts of our Service.",
        },
        {
          term: "Affiliate",
          description:
            'An entity that controls, is controlled by, or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest, or other voting securities.',
        },
        {
          term: "Application",
          description:
            "The software program provided by the Company downloaded by you on any electronic device, named Agora Deng.",
        },
        {
          term: "Company",
          description:
            'Referred to as "We," "Us," or "Our" — Agora Vision Company for Digital Broadcasting and Publishing Ltd., Sulaimani, Iraq.',
        },
        {
          term: "Cookies",
          description:
            "Small files placed on your computer, mobile device, or any other device by a website, containing details of your browsing history and other data.",
        },
        {
          term: "Country",
          description: "Iraq.",
        },
        {
          term: "Device",
          description:
            "Any device that can access the Service, such as a computer, cellphone, or digital tablet.",
        },
        {
          term: "Personal Data",
          description:
            "Any information that relates to an identified or identifiable individual.",
        },
        {
          term: "Service",
          description: "Refers to the Application, the Website, or both.",
        },
        {
          term: "Service Provider",
          description:
            "Any natural or legal person who processes data on behalf of the Company, including third-party companies or individuals used to facilitate or improve the Service.",
        },
        {
          term: "Third-Party Social Media Service",
          description:
            "Any website or social network through which a user can log in or create an account to use the Service.",
        },
        {
          term: "Usage Data",
          description:
            "Data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.",
        },
        {
          term: "Website",
          description:
            "Agora Deng, accessible from http://www.agoradeng.site",
        },
        {
          term: "You",
          description:
            "The individual accessing or using the Service, or the company or legal entity on behalf of which such individual is acting.",
        },
      ],

      personalDataItems: [
        "Email address",
        "First name and last name",
        "Phone number",
        "Address, State, Province, ZIP/Postal code, City",
        "Usage Data",
      ],

      socialProviders: ["Google", "Facebook", "Twitter"],

      trackingTechnologies: [
        {
          name: "Cookies / Browser Cookies",
          description:
            "A small file placed on your device. You can instruct your browser to refuse all Cookies or notify you when a Cookie is being sent. However, refusing Cookies may affect your ability to use parts of our Service.",
        },
        {
          name: "Flash Cookies",
          description:
            "Certain features of our Service may use locally stored objects (Flash Cookies) to collect and store your preferences or activity. Flash Cookies are managed separately from browser cookie settings.",
        },
        {
          name: "Web Beacons",
          description:
            "Small electronic files (also known as clear gifs, pixel tags, or single-pixel gifs) embedded in sections of our Service and emails. They allow us to count users who have visited pages or opened emails, and gather related website statistics.",
        },
      ],

      cookieTypes: [
        {
          name: "Necessary / Essential Cookies",
          type: "Session Cookies",
          purpose:
            "Essential for providing core services on the Website and enabling key features. They authenticate users and prevent fraudulent account use. Without these, certain services cannot be provided.",
        },
        {
          name: "Cookie Policy / Notice Acceptance Cookies",
          type: "Persistent Cookies",
          purpose:
            "Used to identify whether users have accepted the use of cookies on the Website.",
        },
        {
          name: "Functionality Cookies",
          type: "Persistent Cookies",
          purpose:
            "Allow us to remember your choices on the Website, such as login details or language preference, providing a more personalized experience.",
        },
      ],

      dataUses: [
        {
          title: "Service Maintenance",
          description:
            "To provide and maintain our Service, including monitoring usage.",
        },
        {
          title: "Account Management",
          description:
            "To manage your registration as a user and grant access to Service functionalities.",
        },
        {
          title: "Contract Performance",
          description:
            "To fulfill purchase contracts or any other agreements made through the Service.",
        },
        {
          title: "Communication",
          description:
            "To contact you via email, phone, SMS, or push notifications regarding updates, security notices, and service-related communications.",
        },
        {
          title: "Promotions & Offers",
          description:
            "To provide news, special offers, and general information about goods, services, and events similar to those you've previously engaged with, unless you opt out.",
        },
        {
          title: "Request Management",
          description: "To attend to and manage your requests submitted to us.",
        },
        {
          title: "Business Transfers",
          description:
            "To evaluate or conduct mergers, acquisitions, restructuring, or asset sales, where Personal Data may be among the transferred assets.",
        },
        {
          title: "Service Improvement",
          description:
            "For data analysis, identifying trends, evaluating promotional campaigns, and improving our Service and user experience.",
        },
      ],

      dataSharingScenarios: [
        {
          title: "With Service Providers",
          description:
            "We may share your personal information with Service Providers to monitor, analyze, and improve our Service, or to contact you on our behalf.",
        },
        {
          title: "For Business Transfers",
          description:
            "Your data may be shared or transferred during negotiations or completion of mergers, acquisitions, financing, or business sales.",
        },
        {
          title: "With Affiliates",
          description:
            "We may share your information with our affiliates, who are required to honor this Privacy Policy. Affiliates include our parent company, subsidiaries, and joint venture partners.",
        },
        {
          title: "With Business Partners",
          description:
            "We may share your information with business partners to offer you certain products, services, or promotions.",
        },
        {
          title: "With Other Users",
          description:
            "Information you share in public areas of the Service may be visible to other users and potentially distributed publicly. Third-Party Social Media connections may see your name, profile, and activity.",
        },
        {
          title: "With Your Consent",
          description:
            "We may disclose your personal information for any other purpose with your explicit consent.",
        },
      ],

      disclosureScenarios: [
        {
          title: "Business Transactions",
          description:
            "If the Company is involved in a merger, acquisition, or asset sale, your Personal Data may be transferred. We will provide notice before your data becomes subject to a different Privacy Policy.",
        },
        {
          title: "Law Enforcement",
          description:
            "Under certain circumstances, we may be required to disclose your Personal Data if required by law or in response to valid requests by public authorities, such as a court or government agency.",
        },
        {
          title: "Other Legal Requirements",
          description:
            "The Company may disclose your Personal Data in good faith when necessary to:",
          items: [
            "Comply with a legal obligation",
            "Protect and defend the rights or property of the Company",
            "Prevent or investigate possible wrongdoing in connection with the Service",
            "Protect the personal safety of users or the public",
            "Protect against legal liability",
          ],
        },
      ],

      deletionSteps: [
        {
          number: 1,
          title: "Request Process",
          description:
            "Contact our team via phone or email. Please provide your full name and a detailed description of your request.",
        },
        {
          number: 2,
          title: "Verification",
          description:
            "For security purposes, we may ask you to verify your identity before processing the deletion request.",
        },
        {
          number: 3,
          title: "Timely Response",
          description:
            "We are committed to responding to and processing data deletion requests within 30 days.",
        },
        {
          number: 4,
          title: "Rights & Responsibilities",
          description:
            "You have the right to request deletion of your personal data. However, certain legal or contractual obligations may prevent immediate deletion of all data.",
        },
      ],

      contactDetails: [
        {
          label: "Email",
          value: "slaw@agoravision.com",
          href: "mailto:slaw@agoravision.com",
        },
        {
          label: "Website",
          value: "https://www.agoravision.com/",
          href: "https://www.agoravision.com/",
        },
        {
          label: "Phone",
          value: "+964 770 2000 545 | +964 750 2000 545",
          href: null,
        },
        {
          label: "Mailing Address",
          value:
            "Heeran Building, next to Azadi Mall, Offices No. 5 & 6, Sulaimani, Iraq",
          href: null,
        },
      ],
    };
  },
};
</script>